import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  CircularProgress,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  TextField,
  FormControlLabel,
  Pagination,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useStyles } from '../../styles/styles';
import StyledCard from '../../components/cards/StyledCard';
import StyledCardContent from '../../components/cards/StyledCardContent';
import StyledCardHeader, { HeaderButtonIcon } from '../../components/cards/StyledCardHeader';
import { useProvider } from '../../state/providerStore';
import { Api } from '../../api';
import { greenColor } from '../../components/main/react/App';

interface LocationGroup {
  locationId: string;
  locationName: string;
  city: string;
  chargePoints: any[];
}

const groupByLocation = (chargePointRecords: any[]): LocationGroup[] => {
  const map = new Map<string, LocationGroup>();

  for (const record of chargePointRecords) {
    const cp = record.chargePoint;
    if (!cp) continue;

    const locId = cp.locationId || 'unassigned';
    if (!map.has(locId)) {
      map.set(locId, {
        locationId: locId,
        locationName: cp.location?.name || 'Unknown Location',
        city: cp.location?.city || '',
        chargePoints: [],
      });
    }
    map.get(locId)!.chargePoints.push(cp);
  }

  return Array.from(map.values());
};

const PartnerDetailsLocations = observer(({ partnerid }: { partnerid: string }) => {
  const classes = useStyles();
  const assignedProvider = useProvider(undefined, undefined, false);
  const locationsProvider = useProvider(undefined, undefined, false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedLocations, setExpandedLocations] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showRoaming, setShowRoaming] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  useEffect(() => {
    assignedProvider.setRequest(() => Api.charge.listPartnerChargePoints(partnerid));
    assignedProvider.reloadResource();
  }, [partnerid]);

  const assignedRecords = assignedProvider.data || [];
  const assignedGroups = groupByLocation(assignedRecords);
  const totalChargePoints = assignedRecords.length;
  const totalLocations = assignedGroups.length;

  const toggleExpand = (locationId: string) => {
    setExpandedLocations((prev) => {
      const next = new Set(prev);
      if (next.has(locationId)) next.delete(locationId);
      else next.add(locationId);
      return next;
    });
  };

  const handleRemoveChargePoint = async (chargePointId: string) => {
    await Api.charge.removePartnerChargePoint(partnerid, chargePointId);
    assignedProvider.reloadResource();
  };

  const handleRemoveLocation = async (locationGroup: LocationGroup) => {
    const currentIds = assignedRecords.map((r: any) => r.chargePointId);
    const removeIds = new Set(locationGroup.chargePoints.map((cp: any) => cp.id));
    const remaining = currentIds.filter((id: string) => !removeIds.has(id));
    await Api.charge.updatePartnerChargePoints(partnerid, remaining);
    assignedProvider.reloadResource();
  };

  // ─── Add Dialog ─────────────────────────────────────────────────────────

  const loadLocations = (search?: string, includeRoaming?: boolean, pageNum = 0) => {
    const params: any = { skip: pageNum * PAGE_SIZE, take: PAGE_SIZE, sortBy: 'name' };
    if (search) params.search = search;
    if (!includeRoaming) params.excludeRoaming = 'true';
    locationsProvider.setRequest(() => Api.charge.listLocations(undefined, params));
    locationsProvider.reloadResource();
  };

  const openAddDialog = () => {
    const currentIds = new Set<string>(assignedRecords.map((r: any) => r.chargePointId));
    setSelectedIds(currentIds);
    setSearchTerm('');
    setShowRoaming(false);
    setPage(0);
    setDialogOpen(true);
    loadLocations('', false, 0);
  };

  const toggleChargePoint = (cpId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(cpId)) next.delete(cpId);
      else next.add(cpId);
      return next;
    });
  };

  const toggleLocation = (location: any) => {
    const chargePoints = (location.chargePoints || []).filter((cp: any) => !cp.isDeleted);
    const cpIds = chargePoints.map((cp: any) => cp.id);
    const allSelected = cpIds.every((id: string) => selectedIds.has(id));

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        cpIds.forEach((id: string) => next.delete(id));
      } else {
        cpIds.forEach((id: string) => next.add(id));
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Api.charge.updatePartnerChargePoints(partnerid, Array.from(selectedIds));
      assignedProvider.reloadResource();
      setDialogOpen(false);
    } finally {
      setSaving(false);
    }
  };

  // ─── Loading State ──────────────────────────────────────────────────────

  if (assignedProvider.loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '60vh',
        }}
      >
        <CircularProgress style={{ color: greenColor }} />
      </div>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────────

  // Server-side paginated results
  const responseData = locationsProvider.data;
  const pagedLocations = Array.isArray(responseData) ? responseData : responseData?.data || [];
  const totalCount = locationsProvider.count || pagedLocations.length;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <div className={classes.cardRows}>
      <div style={{ width: '100%' }}>
        <StyledCard>
          <StyledCardHeader
            title="Assigned Locations"
            onClick={openAddDialog}
            headerButtonIcon={HeaderButtonIcon.add}
          />
          <StyledCardContent>
            {totalChargePoints > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <Chip
                  label={`${totalChargePoints} charge point${
                    totalChargePoints !== 1 ? 's' : ''
                  } across ${totalLocations} location${totalLocations !== 1 ? 's' : ''}`}
                  size="small"
                  style={{ backgroundColor: greenColor, color: '#fff' }}
                />
              </div>
            )}

            {assignedGroups.length === 0 && (
              <div style={{ color: 'gray', padding: '1rem 0' }}>
                No charge points assigned. Click the + button to add locations.
              </div>
            )}

            {assignedGroups.map((group) => (
              <div
                key={group.locationId}
                style={{
                  borderBottom: '1px solid #eee',
                  padding: '0.5rem 0',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => toggleExpand(group.locationId)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {expandedLocations.has(group.locationId) ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                    <div>
                      <div style={{ fontWeight: 500 }}>{group.locationName}</div>
                      <div style={{ fontSize: '0.85rem', color: 'gray' }}>
                        {group.city} &middot; {group.chargePoints.length} charge point
                        {group.chargePoints.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveLocation(group);
                    }}
                    title="Remove all charge points from this location"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>

                {expandedLocations.has(group.locationId) && (
                  <div style={{ paddingLeft: '2rem', marginTop: '0.5rem' }}>
                    {group.chargePoints.map((cp: any) => (
                      <div
                        key={cp.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0.25rem 0',
                          borderBottom: '1px solid #f5f5f5',
                        }}
                      >
                        <div>
                          <span style={{ fontWeight: 400 }}>{cp.ocppId || cp.name || cp.id}</span>
                          {cp.manufacturer && (
                            <span
                              style={{ color: 'gray', marginLeft: '0.5rem', fontSize: '0.85rem' }}
                            >
                              {cp.manufacturer} {cp.model || ''}
                            </span>
                          )}
                        </div>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveChargePoint(cp.id)}
                          title="Remove charge point"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </StyledCardContent>
        </StyledCard>
      </div>

      {/* Add Charge Points Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Select Charge Points</DialogTitle>
        <DialogContent dividers>
          {/* Search and filter bar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '1rem',
              position: 'sticky',
              top: 0,
              backgroundColor: '#fff',
              zIndex: 1,
              paddingBottom: '0.5rem',
              borderBottom: '1px solid #eee',
            }}
          >
            <TextField
              size="small"
              placeholder="Search by location, city or OCPP ID..."
              value={searchTerm}
              onChange={(e) => {
                const val = e.target.value;
                setSearchTerm(val);
                setPage(0);
                // Debounce: delay server request
                clearTimeout((window as any).__partnerLocSearch);
                (window as any).__partnerLocSearch = setTimeout(() => {
                  loadLocations(val, showRoaming, 0);
                }, 400);
              }}
              style={{ flex: 1 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={showRoaming}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setShowRoaming(val);
                    setPage(0);
                    loadLocations(searchTerm, val, 0);
                  }}
                  size="small"
                  style={{ color: greenColor }}
                />
              }
              label="Show roaming"
              style={{ whiteSpace: 'nowrap' }}
            />
          </div>

          {locationsProvider.loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <CircularProgress style={{ color: greenColor }} />
            </div>
          ) : (
            <div>
              {pagedLocations.length === 0 && (
                <div style={{ color: 'gray', padding: '1rem 0' }}>
                  {searchTerm ? 'No locations match your search.' : 'No locations available.'}
                </div>
              )}
              {pagedLocations.map((location: any) => {
                const chargePoints = (location.chargePoints || []).filter(
                  (cp: any) => !cp.isDeleted,
                );
                if (chargePoints.length === 0) return null;

                const cpIds = chargePoints.map((cp: any) => cp.id);
                const allSelected = cpIds.every((id: string) => selectedIds.has(id));
                const someSelected =
                  !allSelected && cpIds.some((id: string) => selectedIds.has(id));

                return (
                  <div key={location.id} style={{ marginBottom: '0.5rem' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: '#f9f9f9',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                      onClick={() => toggleLocation(location)}
                    >
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        size="small"
                        style={{ color: greenColor }}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleLocation(location)}
                      />
                      <div>
                        <div style={{ fontWeight: 500 }}>{location.name}</div>
                        <div style={{ fontSize: '0.85rem', color: 'gray' }}>
                          {location.city || ''} &middot; {chargePoints.length} charge point
                          {chargePoints.length !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>

                    <div style={{ paddingLeft: '2.5rem' }}>
                      {chargePoints.map((cp: any) => (
                        <div
                          key={cp.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0.25rem 0',
                            cursor: 'pointer',
                          }}
                          onClick={() => toggleChargePoint(cp.id)}
                        >
                          <Checkbox
                            checked={selectedIds.has(cp.id)}
                            size="small"
                            style={{ color: greenColor }}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleChargePoint(cp.id)}
                          />
                          <span>{cp.ocppId || cp.name || cp.id}</span>
                          {cp.manufacturer && (
                            <span
                              style={{ color: 'gray', marginLeft: '0.5rem', fontSize: '0.85rem' }}
                            >
                              {cp.manufacturer} {cp.model || ''}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '0.5rem 0' }}>
            <Pagination
              count={totalPages}
              page={page + 1}
              onChange={(_, p) => {
                setPage(p - 1);
                loadLocations(searchTerm, showRoaming, p - 1);
              }}
              size="small"
            />
          </div>
        )}
        <DialogActions>
          <div style={{ flex: 1, paddingLeft: '1rem', color: 'gray', fontSize: '0.85rem' }}>
            {selectedIds.size} charge point{selectedIds.size !== 1 ? 's' : ''} selected
            {totalCount > 0 && ` · ${totalCount} locations total`}
          </div>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            style={{ backgroundColor: greenColor, color: '#fff' }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
});

export default PartnerDetailsLocations;
